import clientStorage from '@/services/ClientStorage';
import { OFFLINE_STORAGE_NAMESPACE } from './constants';

interface BrowserLicense {
    userUuid: number;
}

const BROWSER_LICENSE_STORAGE_KEY = [OFFLINE_STORAGE_NAMESPACE, 'browser-license'];

/**
 * Rolling TTL after which a browser license is considered expired: offline reading is blocked until the user logs in
 * online again. Downloads are kept until their content expires (see `CONTENT_LICENSE_TTL_MS`), then deleted.
 */
const BROWSER_LICENSE_TTL = 2 * 24 * 60 * 60; // 2 days

/** Rolling TTL after which a downloaded item is considered stale and must be re-downloaded to read. */
const CONTENT_LICENSE_TTL_MS = 7 * 24 * 60 * 60 * 1000; // 7 days

/** How long an expired item stays listed as "Expired" before it is deleted. */
const EXPIRED_ITEM_RETENTION_MS = 7 * 24 * 60 * 60 * 1000; // 7 days

export function readBrowserLicense(): BrowserLicense | null {
    const raw = clientStorage.get<BrowserLicense>(BROWSER_LICENSE_STORAGE_KEY);
    return raw?.userUuid ? raw : null;
}

export function writeBrowserLicense(userId: number): void {
    clientStorage.set(BROWSER_LICENSE_STORAGE_KEY, { userUuid: userId }, BROWSER_LICENSE_TTL);
}

export function clearBrowserLicense(): void {
    clientStorage.remove(BROWSER_LICENSE_STORAGE_KEY);
}

/** Items with a `validatedAt` earlier than this ISO time are expired. */
export function getExpiryCutoff(): string {
    return new Date(Date.now() - CONTENT_LICENSE_TTL_MS).toISOString();
}

/** Items with a `validatedAt` earlier than this ISO time are past retention. */
export function getRetentionCutoff(): string {
    return new Date(Date.now() - CONTENT_LICENSE_TTL_MS - EXPIRED_ITEM_RETENTION_MS).toISOString();
}

export function isItemExpired(validatedAt: string): boolean {
    return validatedAt < getExpiryCutoff();
}
